/**
 * Avatar / AvatarGroup — CAL-14, DS-53.
 *
 * The clinic has no staff photos on day one, so the initials fallback is the
 * normal case, not the degraded one. A provider avatar is filled with the
 * provider's own colour, which is what makes a calendar column scannable.
 */

import { Children, cloneElement, forwardRef, isValidElement, useState } from 'react';
import type { HTMLAttributes, ReactElement } from 'react';
import { readableTextColor } from '@nova/shared';
import { cn, initials } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

const AVATAR_SIZES: Record<AvatarSize, string> = {
  xs: 'h-5 w-5 text-[9px]',
  sm: 'h-6 w-6 text-[10px]',
  md: 'h-8 w-8 text-[11px]',
  lg: 'h-10 w-10 text-xs',
};

// `color` here is a provider/tag hex that drives the background, which is not the
// same thing as the legacy HTML `color` attribute — omit it so the two do not clash.
interface AvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children' | 'color'> {
  /** Full Arabic name — the initials seed and the image's alt text. */
  name: string;
  src?: string | null;
  /** CAL-14 — the provider's hex fills the circle; the text flips for contrast. */
  color?: string | null;
  size?: AvatarSize;
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { name, src, color, size = 'md', className, style, title, ...rest },
  ref,
) {
  // A broken photo URL must not leave an empty circle in a calendar column.
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(src) && !imageFailed;

  const colorStyle =
    color && !showImage ? { backgroundColor: color, color: readableTextColor(color) } : undefined;

  return (
    <span
      ref={ref}
      title={title ?? name}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold leading-none',
        AVATAR_SIZES[size],
        !colorStyle && !showImage && 'bg-slate-200 text-slate-600',
        className,
      )}
      style={colorStyle ? { ...colorStyle, ...style } : style}
      {...rest}
    >
      {showImage ? (
        <img
          src={src ?? undefined}
          alt={name}
          draggable={false}
          onError={() => setImageFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span aria-hidden="true">{initials(name)}</span>
      )}
    </span>
  );
});

interface AvatarGroupProps extends HTMLAttributes<HTMLDivElement> {
  /** How many avatars are drawn before the overflow chip takes over. */
  max?: number;
  /** Applied to every child that has not set its own size. */
  size?: AvatarSize;
}

/**
 * Children must be <Avatar> elements. They overlap on the reading edge (`-ms-2`,
 * so the stack runs right-to-left in Arabic) with a white ring separating them.
 */
export const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>(function AvatarGroup(
  { max = 4, size = 'sm', className, children, ...rest },
  ref,
) {
  const { t } = useTranslation('common');
  const items = Children.toArray(children).filter(isValidElement) as ReactElement<AvatarProps>[];
  const shown = items.slice(0, Math.max(max, 1));
  const overflow = items.length - shown.length;

  return (
    <div ref={ref} className={cn('flex items-center', className)} {...rest}>
      {shown.map((child, index) => (
        <span
          key={child.key ?? index}
          className={cn('inline-flex rounded-full ring-2 ring-white', index > 0 && '-ms-2')}
        >
          {cloneElement(child, { size: child.props.size ?? size })}
        </span>
      ))}

      {overflow > 0 ? (
        <span
          title={t('ui.avatarOverflow', { count: overflow })}
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-full bg-slate-200 font-bold leading-none text-slate-600 ring-2 ring-white',
            AVATAR_SIZES[size],
            shown.length > 0 && '-ms-2',
          )}
        >
          {/* DS-51 — the count is a number, so it stays LTR inside the Arabic UI. */}
          <span className="ltr-num font-mono">+{overflow}</span>
        </span>
      ) : null}
    </div>
  );
});
